import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import "./Dashboard.css";

const EDITABLE = [
  { name: "name",      key: "dashFullName",   type: "text" },
  { name: "specialty", key: "dashSpecialty",  type: "text" },
  { name: "country",   key: "dashCountry",    type: "text" },
  { name: "address",   key: "dashAddress",    type: "text" },
  { name: "telephone", key: "dashPhone",      type: "tel"  },
  { name: "website",   key: "dashWebsite",    type: "url"  },
  { name: "instagram", key: "dashInstagram",  type: "text" },
  { name: "facebook",  key: "dashFacebook",   type: "text" },
];

function StatCard({ value, label, icon }) {
  return (
    <div className="spau-db-stat">
      <span className="spau-db-stat-icon">{icon}</span>
      <span className="spau-db-stat-value">{value}</span>
      <span className="spau-db-stat-label">{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useApp();
  const navigate = useNavigate();

  const stored = JSON.parse(localStorage.getItem("spar_user") || "null");
  const token  = localStorage.getItem("spar_token") || "";

  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(false);
  const [form, setForm]             = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [keptUrls, setKeptUrls]     = useState([]);
  const [newFiles, setNewFiles]     = useState([]);
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState("");
  const [saveErr, setSaveErr]       = useState("");
  const avatarInput = useRef(null);

  useEffect(() => {
    if (!token || !stored?._id) { navigate("/login"); return; }
    axios
      .get(`/api/artists/${stored._id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setProfile(r.data);
        setForm(r.data);
        setKeptUrls(r.data.images || []);
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  const handleFormChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const removeExisting = (url) => setKeptUrls(keptUrls.filter((u) => u !== url));
  const removeNew      = (idx) => setNewFiles(newFiles.filter((_, i) => i !== idx));
  const onAddFiles     = (e) => {
    const slots = 4 - keptUrls.length - newFiles.length;
    if (slots <= 0) return;
    setNewFiles([...newFiles, ...Array.from(e.target.files).slice(0, slots)]);
    e.target.value = "";
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveMsg(""); setSaveErr("");
    try {
      const fd = new FormData();
      const FIELDS = ["name","specialty","country","address","telephone","website","instagram","facebook","shortIntro","biography"];
      for (const key of FIELDS) {
        if (form[key] !== undefined) fd.append(key, form[key]);
      }
      if (avatarFile) fd.append("profilePhoto", avatarFile);
      keptUrls.forEach((url) => fd.append("keepImages", url));
      newFiles.forEach((f) => fd.append("images", f));

      const res = await axios.put("/api/artists/me", fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const updated = res.data.artist || res.data;
      setProfile(updated);
      setForm(updated);
      setKeptUrls(updated.images || []);
      setNewFiles([]);
      setAvatarFile(null);
      setAvatarPreview(null);
      setSaveMsg(t("dashSaved"));
      setEditing(false);
    } catch (err) {
      setSaveErr(err.response?.data?.message || t("dashSaveErr"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("spar_user");
    localStorage.removeItem("spar_token");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="spau-db-loading">
        <div className="spau-db-spinner" />
        <span>{t("dashLoading")}</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="spau-db-loading">
        <p>{t("dashLoadErr")}</p>
        <a href="/" className="btn btn-primary" style={{ marginTop: 16 }}>{t("dashGoHome")}</a>
      </div>
    );
  }

  const avatarSrc = avatarPreview || profile.profilePhoto;
  const dir = lang === "ar" ? "rtl" : "ltr";

  const thumbStyle = { width:"100%", aspectRatio:"1/1", objectFit:"cover", borderRadius:8, border:"1px solid var(--line)", display:"block" };
  const removeStyle = { position:"absolute", top:4, right:4, background:"rgba(0,0,0,0.55)", color:"#fff", border:"none", borderRadius:"50%", width:22, height:22, fontSize:"0.9rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0 };
  const newBadge = { position:"absolute", bottom:4, left:4, background:"var(--brand)", color:"#fff", fontSize:"0.58rem", fontWeight:700, padding:"2px 5px", borderRadius:4 };
  const addSlot = { aspectRatio:"1/1", border:"2px dashed var(--line)", borderRadius:8, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"var(--muted)", gap:4 };

  return (
    <div className="spau-db-page" dir={dir}>

      {/* ── Sidebar ── */}
      <aside className="spau-db-sidebar">
        <div className="spau-db-avatar-wrap">
          <img
            src={avatarSrc || "/placeholder.jpg"}
            alt={profile.name}
            className="spau-db-avatar"
            onError={(e) => { e.target.src = ""; e.target.style.background = "var(--line)"; }}
          />
          {editing && (
            <>
              <button
                className="spau-db-avatar-btn"
                type="button"
                onClick={() => avatarInput.current?.click()}
                title="Change photo"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              <input ref={avatarInput} type="file" accept="image/*" style={{ display:"none" }} onChange={handleAvatarChange} />
            </>
          )}
        </div>

        <h2 className="spau-db-name">{profile.name}</h2>
        <p className="spau-db-specialty">{profile.specialty}</p>
        {profile.country && (
          <span className="spau-db-country">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {profile.country}
          </span>
        )}

        <nav className="spau-db-nav">
          <a href={`/artists/${profile._id}`} className="spau-db-nav-link" target="_blank" rel="noopener noreferrer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {t("dashViewProfile")}
          </a>
          <button className="spau-db-nav-link" onClick={() => { setEditing(!editing); setSaveMsg(""); setSaveErr(""); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            {editing ? t("dashCancelEdit") : t("dashEditProfile")}
          </button>
          <button className="spau-db-nav-link spau-db-nav-logout" onClick={handleLogout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {t("dashSignOut")}
          </button>
        </nav>
      </aside>

      {/* ── Main ── */}
      <div className="spau-db-main">

        <div className="spau-db-stats">
          <StatCard value={profile.images?.length ?? 0} label={t("dashArtworks")}  icon="🎨" />
          <StatCard value={profile.country || "—"}       label={t("dashLocation")}  icon="📍" />
          <StatCard value={profile.specialty}            label={t("dashSpecialty")} icon="✦"  />
          <StatCard value={profile.email}                label={t("dashEmail")}     icon="✉"  />
        </div>

        {saveMsg && <div className="spau-db-alert spau-db-alert-ok">{saveMsg}</div>}
        {saveErr && <div className="spau-db-alert spau-db-alert-err">{saveErr}</div>}

        {editing ? (
          <form className="spau-db-form" onSubmit={handleSave}>
            <h3 className="spau-db-section-title">{t("dashEditTitle")}</h3>

            <div className="spau-db-form-grid">
              {EDITABLE.map((f) => (
                <div className="field" key={f.name}>
                  <label>{t(f.key)}</label>
                  <input className="input" type={f.type} name={f.name} value={form[f.name] || ""} onChange={handleFormChange} />
                </div>
              ))}
            </div>

            <div className="field">
              <label>{t("dashShortIntro")}</label>
              <textarea className="textarea" name="shortIntro" rows={3} value={form.shortIntro || ""} onChange={handleFormChange} />
            </div>

            <div className="field">
              <label>{t("dashFullBio")}</label>
              <textarea className="textarea" name="biography" rows={6} value={form.biography || ""} onChange={handleFormChange} />
            </div>

            <div className="field">
              <label style={{ display:"flex", justifyContent:"space-between" }}>
                <span>{t("dashArtworks")}</span>
                <span style={{ fontWeight:400, color:"var(--muted)", fontSize:"0.8rem" }}>{keptUrls.length + newFiles.length}/4</span>
              </label>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginTop:8 }}>
                {keptUrls.map((url, i) => (
                  <div key={`k-${i}`} style={{ position:"relative" }}>
                    <img src={url} alt={`work ${i+1}`} style={thumbStyle} />
                    <button type="button" onClick={() => removeExisting(url)} style={removeStyle}>×</button>
                  </div>
                ))}
                {newFiles.map((f, i) => (
                  <div key={`n-${i}`} style={{ position:"relative" }}>
                    <img src={URL.createObjectURL(f)} alt={`new ${i+1}`} style={{ ...thumbStyle, outline:"2px solid var(--brand)" }} />
                    <button type="button" onClick={() => removeNew(i)} style={removeStyle}>×</button>
                    <span style={newBadge}>new</span>
                  </div>
                ))}
                {keptUrls.length + newFiles.length < 4 && (
                  <label style={addSlot}>
                    <span style={{ fontSize:"1.5rem" }}>+</span>
                    <span style={{ fontSize:"0.7rem" }}>{t("dashManagePhotos")}</span>
                    <input type="file" accept="image/*" multiple style={{ display:"none" }} onChange={onAddFiles} />
                  </label>
                )}
              </div>
            </div>

            <div style={{ display:"flex", gap:12, marginTop:8 }}>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex:1, justifyContent:"center" }}>
                {saving ? t("dashSaving") : t("dashSave")}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setEditing(false); setForm(profile); setKeptUrls(profile.images||[]); setNewFiles([]); setAvatarPreview(null); setAvatarFile(null); }}>
                {t("dashCancel")}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="spau-db-section">
              <h3 className="spau-db-section-title">{t("dashAbout")}</h3>
              {profile.shortIntro && <p className="spau-db-intro">{profile.shortIntro}</p>}
              {profile.biography  && <p className="spau-db-bio">{profile.biography}</p>}
            </div>

            <div className="spau-db-section">
              <h3 className="spau-db-section-title">{t("dashContactLinks")}</h3>
              <div className="spau-db-contact">
                {profile.telephone && <div className="spau-db-contact-row"><span>{t("dashPhone")}</span><span>{profile.telephone}</span></div>}
                {profile.email     && <div className="spau-db-contact-row"><span>{t("dashEmail")}</span><span>{profile.email}</span></div>}
                {profile.address   && <div className="spau-db-contact-row"><span>{t("dashAddress")}</span><span>{profile.address}</span></div>}
                {profile.website   && <div className="spau-db-contact-row"><span>{t("dashWebsite")}</span><a href={profile.website} target="_blank" rel="noopener noreferrer">{profile.website}</a></div>}
                {profile.instagram && <div className="spau-db-contact-row"><span>{t("dashInstagram")}</span><a href={`https://instagram.com/${profile.instagram.replace("@","")}`} target="_blank" rel="noopener noreferrer">{profile.instagram}</a></div>}
                {profile.facebook  && <div className="spau-db-contact-row"><span>{t("dashFacebook")}</span><a href={profile.facebook} target="_blank" rel="noopener noreferrer">{profile.facebook}</a></div>}
              </div>
            </div>
          </>
        )}

        {profile.images?.length > 0 && (
          <div className="spau-db-section">
            <h3 className="spau-db-section-title">{t("dashPortfolio")} ({profile.images.length})</h3>
            <div className="spau-db-portfolio">
              {profile.images.map((img, i) => (
                <div key={i} className="spau-db-portfolio-item">
                  <img src={img} alt={`Artwork ${i + 1}`} className="spau-db-portfolio-img" onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
